import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useOrdersContext } from '@/contexts/OrderContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const RestaurantSelector = ({ onChange, isInline = false }) => {
    const [restaurants, setRestaurants] = useState([]);
    const { getRestaurantId, cart } = useOrdersContext();
    const [currentRestaurantId, setCurrentRestaurantId] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);

    // Détection si l'écran est mobile
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const publicResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/restaurants`);
                if (publicResponse.status === 200) {
                    console.log("Utilisation de l'API publique (sans priorités)", publicResponse.data);

                    // Tri manuel par nom puisque les priorités ne sont pas disponibles
                    const manualSortedRestaurants = [...publicResponse.data].sort((a, b) => a.priority - b.priority);

                    console.log("Restaurants triés manuellement:", manualSortedRestaurants);
                    setRestaurants(manualSortedRestaurants);

                    const restaurantId = await getRestaurantId();
                    setCurrentRestaurantId(restaurantId);
                }
            } catch (fallbackError) {
                console.error('Error fetching restaurants:', fallbackError);
            }
        };
        fetchRestaurants();
    }, []);

    useEffect(() => {
        if (cart.restaurant_id && cart.restaurant_id !== currentRestaurantId) {
            const restaurantId = cart.restaurant_id;
            setCurrentRestaurantId(restaurantId);
        }
    }, [currentRestaurantId, cart.restaurant_id]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    const getButtonClass = (id) =>
        `flex items-center justify-center font-bold rounded-lg px-4 py-2 text-center text-sm shadow-xl transition-colors duration-200 ${id === currentRestaurantId
            ? 'bg-primary text-white border-2 border-primary'
            : 'bg-white text-primary border-2 border-primary hover:bg-primary hover:text-white'
        }`;

    const handleButtonClick = (restaurant) => {
        setCurrentRestaurantId(restaurant.id);
        onChange(restaurant);
        setDropdownOpen(false);
    };

    const firstRestaurants = restaurants.slice(0, 4);
    const moreRestaurants = restaurants.slice(4);
    const selectedInDropdown = moreRestaurants.find(r => r.id === currentRestaurantId);

    // Version mobile avec Swiper
    if (isMobile) {
        // Style spécifique pour mobile - plus petit, plus compact
        const getMobileButtonClass = (id) =>
            `flex items-center justify-center font-bold rounded-lg px-2 py-1 text-center text-xs shadow-md transition-colors duration-200 ${id === currentRestaurantId
                ? 'bg-primary text-white border border-primary'
                : 'bg-white text-primary border border-primary hover:bg-primary hover:text-white'
            }`;

        // Adapter le nombre de slides visibles selon le nombre de restaurants
        const getSlidesPerView = () => {
            if (restaurants.length === 1) return 1;
            if (restaurants.length === 2) return 2;
            return 2.5; // Pour 3+ restaurants, montrer 2.5 slides pour indiquer qu'on peut swiper
        };

        return (
            <div className={`restaurant-selector p-2 ${isInline ? 'w-full' : ''}`}>
                <div className="flex justify-center">
                    <Swiper
                        spaceBetween={8}
                        slidesPerView={getSlidesPerView()}
                        modules={[Virtual]}
                        virtual
                        grabCursor={true}
                        touchRatio={1.5}
                        className="max-w-md"
                        centeredSlides={restaurants.length === 1}
                    >
                        {restaurants.map((restaurant) => (
                            <SwiperSlide key={restaurant.id}>
                                <button
                                    className={getMobileButtonClass(restaurant.id)}
                                    onClick={() => handleButtonClick(restaurant)}
                                    style={{ width: '100%', margin: '0 auto' }}
                                >
                                    {restaurant.name.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </button>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        );
    }

    // Version desktop avec boutons + dropdown
    return (
        <div className={`restaurant-selector p-4 md:px-2 md:py-0 ${isInline ? 'md:landscape:w-full' : ''} lg:w-full lg:mx-auto lg:relative xl:p-8 xl:pb-2`} id='restaurant-selector'>
            {dropdownOpen && (
                <div className="mb-4 text-center font-semibold text-lg text-primary">Merci de sélectionner pour quelle occasion vous voulez commander</div>
            )}
            <div className="flex gap-3 flex-wrap justify-center items-center relative max-w-4xl mx-auto">
                {firstRestaurants.map((restaurant) => (
                    <button
                        key={restaurant.id}
                        className={getButtonClass(restaurant.id)}
                        onClick={() => handleButtonClick(restaurant)}
                        style={{ minWidth: '200px', maxWidth: '280px', flexGrow: 1, flexBasis: '30%' }}
                    >
                        {restaurant.name.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </button>
                ))}
                {moreRestaurants.length > 0 && (
                    <div className="relative" ref={dropdownRef}>
                        <button
                            className={getButtonClass(selectedInDropdown ? selectedInDropdown.id : 'more') + ' flex-nowrap'}
                            onClick={() => setDropdownOpen((open) => !open)}
                            style={{ minWidth: '200px', maxWidth: '280px', flexGrow: 1, flexBasis: '30%' }}
                        >
                            {selectedInDropdown
                                ? selectedInDropdown.name.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                                : 'Plus'}
                            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {dropdownOpen && (
                            <div className="absolute z-30 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg">
                                {moreRestaurants.map((restaurant) => (
                                    <button
                                        key={restaurant.id}
                                        className={`block w-full text-left px-4 py-2 hover:bg-primary hover:text-white ${currentRestaurantId === restaurant.id ? 'bg-primary text-white' : 'text-primary'}`}
                                        onClick={() => handleButtonClick(restaurant)}
                                    >
                                        {restaurant.name.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RestaurantSelector;
